import {useCallback} from 'react'
import {AuthHelpers, useAuthHelper, useAccessToken} from '@salesforce/commerce-sdk-react'
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext'
import {helpers} from 'commerce-sdk-isomorphic'
import {useQueryClient} from '@tanstack/react-query'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useGigya} from '../context/gigya'
import {getMrtApiBasepath} from '../ssr/helpers'
import {
    SA_EMAIL_KEY,
    SA_IMPERSONATING_KEY,
    SA_TOKEN_KEY,
    SA_SESSION_TOKEN_KEY,
} from './use-sales-assistant-session'

/**
 * Fully logs a Sales Assistant agent out:
 *   1. ends the B2C agent session on the SCAPI side (which also ends any
 *      "login on behalf" customer session) and empties the cart;
 *   2. clears the InMemoryStorage markers (done server-side in step 1);
 *   3. revokes the SLAS shopper token client-side: auth.logout() revokes the
 *      registered (customer-on-behalf) refresh token, and we additionally make an
 *      explicit SLAS logout for the guest refresh token — auth.logout() skips
 *      guest tokens, and SLAS treats each session type as separate, so the agent's
 *      working guest token would otherwise survive end of session. Also ends Gigya;
 *   4. returns the operator to /sales-assistant/login.
 *
 * Order matters: the SCAPI logout (which clears the session markers) is awaited
 * before navigating, so the /sales-assistant/login page's check-session sees no markers
 * and no agent email and therefore does not bounce back to the dashboard. The
 * SLAS logout runs after the navigation so the token swap can't race the
 * already-mounted login page's session check.
 *
 * Returns an async `agentLogout({onStart, onSettled})` function; the callbacks
 * let the caller drive its own loading indicator.
 */
export const useAgentLogout = () => {
    const {gigyaObject} = useGigya()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const {getTokenWhenReady} = useAccessToken()
    const auth = useAuthContext()
    const queryClient = useQueryClient()
    const navigate = useNavigation()

    return useCallback(
        async ({onStart, onSettled} = {}) => {
            onStart?.()

            let salesAssistantSessionToken = null
            // Capture the guest refresh token (cookie cc-nx-g) up-front, before any
            // logout clears storage. It is present only when the agent is browsing
            // as guest (not impersonating); during impersonation the registered
            // token replaces it, so this is null and the guest revocation is skipped.
            let guestRefreshToken = null
            try {
                salesAssistantSessionToken = localStorage.getItem(SA_SESSION_TOKEN_KEY)
            } catch {
                // localStorage may be unavailable in some environments
            }
            try {
                guestRefreshToken = auth.get('refresh_token_guest')
            } catch {
                // auth storage may be unavailable
            }

            // End the B2C agent session, empty the cart and clear the server-side
            // session markers while the current token is still valid. Best-effort:
            // a failure here must not strand the agent in a half-logged-out state,
            // so we proceed to revoke the token regardless.
            try {
                const token = await getTokenWhenReady()
                await fetch(origin + getMrtApiBasepath('/sales-assistant-logout'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({salesAssistantSessionToken}),
                })
            } catch (err) {
                console.error('[SA:agentLogout]', err)
            }

            // Clear the local markers and hide the banner immediately. The session
            // markers are already gone server-side (above), so the /sales-assistant/login
            // page below won't treat this as an active session.
            try {
                localStorage.removeItem(SA_EMAIL_KEY)
                localStorage.removeItem(SA_IMPERSONATING_KEY)
                localStorage.removeItem(SA_TOKEN_KEY)
                localStorage.removeItem(SA_SESSION_TOKEN_KEY)
                window.dispatchEvent(new CustomEvent('sa-impersonation-changed'))
                window.dispatchEvent(new CustomEvent('sa-session-ended'))
            } catch {
                // localStorage may be unavailable in some environments
            }

            navigate('/sales-assistant/login')

            // Explicitly revoke the guest refresh token via SLAS /oauth2/logout.
            // auth.logout() below only revokes registered tokens, so without this
            // the agent's working guest token would survive end of session.
            if (guestRefreshToken) {
                try {
                    await helpers.logout({
                        slasClient: auth.client,
                        parameters: {
                            accessToken: auth.get('access_token'),
                            refreshToken: guestRefreshToken,
                        },
                    })
                } catch {
                    // Best-effort: the token may already be invalid/expired.
                }
            }

            // Revoke the SLAS shopper token (mints a fresh guest token) and end the
            // Gigya session. Done after navigation so the token swap can't race the
            // login page's session check. When impersonating, auth.logout() revokes
            // the registered (customer-on-behalf) refresh token.
            gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
            try {
                await logout.mutateAsync()
            } catch {
                // The token may already be invalid; the markers are cleared regardless.
            }

            await queryClient.invalidateQueries()
            onSettled?.()
        },
        [gigyaObject, logout, getTokenWhenReady, auth, queryClient, navigate],
    )
}
